<div class="page-container">
  <div class="page-header">
    <div>
      <h1>Agent Management</h1>
      <p class="subtitle">Configure AI agents, system instructions, and capabilities.</p>
    </div>
    <div class="controls">
      <button class="action-btn" (click)="openAddModal()">+ Add Agent</button>
    </div>
  </div>

  <!-- Blueprint Cache Status Card -->
  <div class="cache-status-card" *ngIf="blueprintCache">
    <div class="cache-card-header">
      <div class="cache-badge">⚡ Gemini Context Cache Active</div>
      <span class="cache-model font-mono">{{ blueprintCache.model }}</span>
    </div>
    <div class="cache-details">
      <strong>{{ blueprintCache.totalTokens | number }} tokens</strong> pre-cached from 
      <strong>{{ blueprintCache.fileCount }} blueprints</strong> in <code>Platform/features/</code>
    </div>
    <div class="cache-meta-row">
      <span class="cache-resource font-mono">{{ blueprintCache.name }}</span>
      <span class="cache-expiry">Expires: {{ blueprintCache.expireTime | date:'medium' }}</span>
    </div>
  </div>

  <div class="card no-padding">
    <table class="data-table">
      <thead>
        <tr>
          <th class="text-left">Agent ID</th>
          <th class="text-left">Agent Name</th>
          <th class="text-left">Description</th>
          <th class="text-center">Status</th>
          <th class="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let agent of agents$ | async">
          <td class="fw-500 font-mono">{{ agent.agentId }}</td>
          <td class="fw-500 name-col">
            <div class="agent-name-cell">
              <span class="agent-icon-bubble">{{ resolveIcon(agent) }}</span>
              <div class="agent-name-group">
                <span class="agent-name-text">{{ agent.name }}</span>
                <span class="agent-badge-pill">{{ resolveBadge(agent) }}</span>
              </div>
            </div>
          </td>
          <td class="desc-col">
            <div class="desc-cell-wrapper">
              <span class="desc-truncated">{{ agent.description || '—' }}</span>
              <div class="custom-tooltip" *ngIf="agent.description">
                {{ agent.description }}
              </div>
            </div>
          </td>
          <td class="text-center">
            <span class="status" [class.status-active]="agent.isActive" [class.status-inactive]="!agent.isActive">
              {{ agent.isActive ? 'Active' : 'Inactive' }}
            </span>
          </td>
          <td class="text-center">
            <button class="action-btn view-btn" (click)="editAgent(agent)">Edit</button>
            <button class="action-btn cancel" (click)="deleteAgent(agent.agentId)">Delete</button>
          </td>
        </tr>
        <tr *ngIf="(agents$ | async)?.length === 0">
          <td colspan="5" class="text-center empty-table-msg">
            No agents found. Click "+ Add Agent" to create a new agent.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Add/Edit Modal -->
<div class="modal-overlay" *ngIf="isModalOpen" (click)="closeModal()">
  <div class="modal-content large-modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>{{ editingId ? 'Edit Agent: ' + editingId : 'Add Agent' }}</h2>
      <button class="close-btn" (click)="closeModal()">&times;</button>
    </div>
    
    <div class="modal-body">
      <div class="form-group">
        <label>Agent ID <span class="required">*</span></label>
        <input 
          type="text" 
          class="form-control font-mono" 
          [(ngModel)]="currentAgent.agentId" 
          [disabled]="!!editingId"
          placeholder="e.g., financial-operations">
        <small class="help-text">Unique identifier. Used as the Firestore document ID (agents/&#123;agentId&#125;).</small>
      </div>

      <div class="form-row-two-col">
        <div class="form-group">
          <label>Agent Name <span class="required">*</span></label>
          <input type="text" class="form-control" [(ngModel)]="currentAgent.name" placeholder="e.g., Financial & Operations Agent">
        </div>
        <div class="form-group">
          <label>Icon Emoji</label>
          <input type="text" class="form-control" [(ngModel)]="currentAgent.icon" [placeholder]="resolveIcon(currentAgent)">
          <small class="help-text">Leave blank to auto-detect icon (🏢, 💰, 📸, 🤖, etc.).</small>
        </div>
      </div>
      
      <div class="form-row-two-col">
        <div class="form-group">
          <label>Category Badge</label>
          <input type="text" class="form-control" [(ngModel)]="currentAgent.badge" [placeholder]="resolveBadge(currentAgent)">
          <small class="help-text">Leave blank to auto-assign badge category.</small>
        </div>
        <div class="form-group checkbox-group align-checkbox">
          <label>
            <input type="checkbox" [(ngModel)]="currentAgent.isActive">
            Active Status
          </label>
        </div>
      </div>

      <div class="form-group">
        <label>Description</label>
        <input type="text" class="form-control" [(ngModel)]="currentAgent.description" placeholder="Brief description of the agent's role">
      </div>
      
      <div class="form-group">
        <label>Metaprompt (System Instruction) <span class="required">*</span></label>
        <textarea class="form-control metaprompt-textarea" [(ngModel)]="currentAgent.metaprompt" placeholder="You are an expert ERP AI assistant..."></textarea>
        <small class="help-text">Sets the primary context, calculation rules, and behavior for this AI agent.</small>
      </div>
    </div>

    <div class="modal-actions">
      <button class="action-btn cancel-outline" (click)="closeModal()" [disabled]="isSaving">Cancel</button>
      <button class="action-btn" (click)="saveAgent()" [disabled]="isSaving">{{ isSaving ? 'Saving...' : 'Save Agent' }}</button>
    </div>
  </div>
</div>